import { yupResolver } from '@hookform/resolvers/yup';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import ControlledDatePicker from '../../components/BasicUI/ControlledInputs/ControlledDatePicker';
import ControlledPhoneNumberInput from '../../components/BasicUI/ControlledInputs/ControlledPhoneNumberInput';
import ControlledRadioGroup from '../../components/BasicUI/ControlledInputs/ControlledRadioGroup';
import ControlledTextField from '../../components/BasicUI/ControlledInputs/ControlledTextField';
import { PHONE_COUNTRY_CODES } from '../../components/BasicUI/Input/PhoneNumberInput';
import { detectPhoneCountry } from '../../util/phoneCountry';
import {
  CALLBACK_SLOTS,
  callbackDateBounds,
  isSelectableCallbackDate,
  isWeekend,
} from './callback';

const DEFAULT_VALUES = {
  name: '',
  email: '',
  phone: '',
  callbackDate: null,
  callbackSlot: '',
  message: '',
  website: '',
};

const CALLBACK_DATE_FORMAT = 'YYYY-MM-DD';

// A phone field that only holds the dial code (e.g. "+49", or a still incomplete "+" / "+4"
// while typing it) counts as empty.
const DIAL_CODE_ONLY = /^\+\d{0,4}$/;

/**
 * True once the phone field holds more than the bare dial code. Only then the callback
 * day and slot are shown and required.
 * @param {string} [value]
 */
export const isPhoneGiven = (value) => !!value && !DIAL_CODE_ONLY.test(value.replace(/\s/g, ''));

const radioGroupProps = { row: true };

const messageTextFieldProps = { multiline: true, minRows: 4 };

// The honeypot has to stay a focusable-looking text input in the DOM (bots skip
// type="hidden"), so it is moved off-screen instead of being hidden.
const honeypotSx = {
  position: 'absolute',
  left: '-10000px',
  top: 'auto',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
};

const fieldErrorProps = (errors, name) => ({
  error: !!errors[name],
  helperText: errors[name]?.message ?? '',
});

const createLabels = (intl) => ({
  name: intl.formatMessage({ id: 'ContactForm.nameLabel' }),
  nameRequired: intl.formatMessage({ id: 'ContactForm.nameRequired' }),
  email: intl.formatMessage({ id: 'ContactForm.emailLabel' }),
  emailRequired: intl.formatMessage({ id: 'ContactForm.emailRequired' }),
  emailValid: intl.formatMessage({ id: 'ContactForm.emailValid' }),
  phone: intl.formatMessage({ id: 'ContactInquiryForm.phoneLabel' }),
  phoneHint: intl.formatMessage({ id: 'ContactInquiryForm.phoneHint' }),
  callbackDate: intl.formatMessage({ id: 'ContactInquiryForm.callbackDateLabel' }),
  callbackDateRequired: intl.formatMessage({ id: 'ContactInquiryForm.callbackDateRequired' }),
  callbackSlot: intl.formatMessage({ id: 'ContactInquiryForm.callbackSlotLabel' }),
  callbackSlotRequired: intl.formatMessage({ id: 'ContactInquiryForm.callbackSlotRequired' }),
  message: intl.formatMessage({ id: 'ContactInquiryForm.messageLabel' }),
  submitDefault: intl.formatMessage({ id: 'ContactInquiryForm.submitDefault' }),
});

const createSlotOptions = (intl) => [
  { value: 'morning', label: intl.formatMessage({ id: 'ContactInquiryForm.slotMorning' }) },
  { value: 'midday', label: intl.formatMessage({ id: 'ContactInquiryForm.slotMidday' }) },
  { value: 'afternoon', label: intl.formatMessage({ id: 'ContactInquiryForm.slotAfternoon' }) },
];

// name and email are always required; callbackDate and callbackSlot only while the phone
// number is given, which is exactly when they are rendered. A required field that is not
// rendered would block the submit silently, so keep schema and JSX in sync.
const createSchema = (labels) =>
  yup.object().shape({
    name: yup.string().trim().required(labels.nameRequired),
    email: yup.string().trim().email(labels.emailValid).required(labels.emailRequired),
    phone: yup.string(),
    callbackDate: yup
      .mixed()
      .nullable()
      .when('phone', ([phone], schema) =>
        isPhoneGiven(phone)
          ? schema.test('callback-date', labels.callbackDateRequired, (day) =>
              isSelectableCallbackDate(day)
            )
          : schema
      ),
    callbackSlot: yup
      .string()
      .when('phone', ([phone], schema) =>
        isPhoneGiven(phone)
          ? schema
              .oneOf(CALLBACK_SLOTS, labels.callbackSlotRequired)
              .required(labels.callbackSlotRequired)
          : schema
      ),
    message: yup.string(),
    website: yup.string(),
  });

/**
 * Converts the validated form values to the submit payload: the callback day becomes a
 * plain YYYY-MM-DD string. Without a phone number the callback values are cleared, even if
 * they were filled before the phone number was removed again. A bare dial code counts as no
 * phone number and is sent as an empty string, like the server treats it.
 * @param {{phone: string, callbackDate: import('moment').Moment | null, callbackSlot: string} & Record<string, any>} values
 */
export const toSubmitValues = (values) =>
  isPhoneGiven(values.phone)
    ? { ...values, callbackDate: values.callbackDate.format(CALLBACK_DATE_FORMAT) }
    : { ...values, phone: '', callbackDate: '', callbackSlot: '' };

// The label sits above the picker like the label of the text fields (Stack spacing 1).
const dateLabelProps = { sx: { mb: 1 } };

const createDatePickerProps = (label, hasError) => ({
  ...callbackDateBounds(),
  shouldDisableDate: isWeekend,
  // Replaces the default slotProps of ControlledDatePicker, so the size is repeated here. The
  // visible label is a FormLabel legend that is not linked to the input, hence the aria-label.
  slotProps: {
    textField: {
      size: 'small',
      fullWidth: true,
      required: true,
      error: hasError,
      inputProps: { 'aria-label': label },
    },
  },
});

/**
 * The phone country of the visitor, read from the browser languages after the mount. The
 * server and the first client render use DE, so the SSR markup hydrates without a mismatch.
 * @param {string} locale - the page locale, used when no browser language maps to a country
 * @returns {string} ISO code
 */
const useBrowserPhoneCountry = (locale) => {
  const [country, setCountry] = useState('DE');

  useEffect(() => {
    setCountry(
      detectPhoneCountry({
        languages: navigator.languages ?? [navigator.language],
        locale,
        supported: PHONE_COUNTRY_CODES,
      })
    );
  }, [locale]);

  return country;
};

const CallbackDateFieldComponent = ({ control, errors, label }) => {
  const hasDateError = !!errors.callbackDate;
  const datePickerProps = useMemo(
    () => createDatePickerProps(label, hasDateError),
    [label, hasDateError]
  );

  return (
    <ControlledDatePicker
      control={control}
      datePickerProps={datePickerProps}
      formLabelProps={dateLabelProps}
      fullWidth
      {...fieldErrorProps(errors, 'callbackDate')}
      label={label}
      name="callbackDate"
      required
    />
  );
};

const CallbackDateField = memo(CallbackDateFieldComponent);

/**
 * ContactInquiryForm is the contact form of the Storyblok contact block for host prospects.
 * @param {object} props
 * @param {(values: {name: string, email: string, phone: string, callbackDate: string, callbackSlot: 'morning' | 'midday' | 'afternoon' | '', message: string, website: string}) => void | Promise<any>} props.onSubmit - called with the validated values; callbackDate and callbackSlot are empty strings without a phone number; a returned Promise keeps the form busy until it settles
 * @param {boolean} props.inProgress - true while the request is running, disables the submit button
 * @param {string} [props.submitLabel] - label of the submit button, defaults to the translated default
 * @param {React.ReactNode} [props.privacyNotice] - notice rendered below the submit button
 */
const ContactInquiryFormComponent = ({
  onSubmit,
  inProgress = false,
  submitLabel = '',
  privacyNotice = null,
}) => {
  const intl = useIntl();
  const labels = useMemo(() => createLabels(intl), [intl]);
  const resolver = useMemo(() => yupResolver(createSchema(labels)), [labels]);
  const slotOptions = useMemo(() => createSlotOptions(intl), [intl]);
  const phoneCountry = useBrowserPhoneCountry(intl.locale);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: DEFAULT_VALUES, resolver });

  const phone = useWatch({ control, name: 'phone' });
  // The callback fields are only mounted (and only required) while a phone number is given.
  const showCallback = isPhoneGiven(phone);

  return (
    <Stack
      component="form"
      noValidate
      onSubmit={handleSubmit((values) => onSubmit(toSubmitValues(values)))}
      spacing={2}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ControlledTextField
            control={control}
            fullWidth
            {...fieldErrorProps(errors, 'name')}
            label={labels.name}
            name="name"
            required
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ControlledTextField
            control={control}
            fullWidth
            {...fieldErrorProps(errors, 'email')}
            label={labels.email}
            name="email"
            required
            textFieldProps={{ type: 'email' }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ControlledPhoneNumberInput
            control={control}
            defaultCountry={phoneCountry}
            fullWidth
            helperText={labels.phoneHint}
            label={labels.phone}
            name="phone"
            prefillDialCode
          />
        </Grid>
        {showCallback ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CallbackDateField control={control} errors={errors} label={labels.callbackDate} />
          </Grid>
        ) : null}
        {showCallback ? (
          <Grid size={12}>
            <ControlledRadioGroup
              control={control}
              {...fieldErrorProps(errors, 'callbackSlot')}
              label={labels.callbackSlot}
              name="callbackSlot"
              options={slotOptions}
              radioGroupProps={radioGroupProps}
              required
            />
          </Grid>
        ) : null}
        <Grid size={12}>
          <ControlledTextField
            control={control}
            fullWidth
            {...fieldErrorProps(errors, 'message')}
            label={labels.message}
            name="message"
            textFieldProps={messageTextFieldProps}
          />
        </Grid>
      </Grid>
      <Box aria-hidden="true" sx={honeypotSx}>
        <Box
          {...register('website')}
          aria-hidden="true"
          autoComplete="off"
          component="input"
          tabIndex={-1}
          type="text"
        />
      </Box>
      <Button
        color="primary"
        disabled={inProgress || isSubmitting}
        loading={inProgress || isSubmitting}
        type="submit"
        variant="contained"
      >
        {submitLabel || labels.submitDefault}
      </Button>
      {privacyNotice}
    </Stack>
  );
};

ContactInquiryFormComponent.displayName = 'ContactInquiryForm';

const ContactInquiryForm = memo(ContactInquiryFormComponent);

export default ContactInquiryForm;
